import {
  ALL_COLLECTIONS,
  getCollection,
  parentPointer,
  type AppNotification,
  type FamilySummary,
  type MutationResult,
  type PullResponse,
  type PushResponse,
} from '@app/shared';
import { api, ApiError, OfflineError } from '../api.ts';
import { db, getMeta, META, setMeta, type OutboxEntry } from '../db.ts';
import { processUploads } from '../files/uploads.ts';
import { learnServerTime } from './clock.ts';
import { pendingPatch } from './outbox.ts';

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'error';

export interface SyncStatus {
  phase: SyncPhase;
  lastSyncedAt: number | null;
  message: string | null;
}

export interface SyncStartOptions {
  families: FamilySummary[];
  onMembershipChange(): void;
  onFamilyGone(familyId: string): Promise<void>;
}

type Listener = () => void;

/**
 * Keeps the local replica and the server in step:
 *  1. push queued mutations (idempotent, in order)
 *  2. pull changes since the per-family cursor (permission-filtered by the server)
 *  3. upload files whose records now exist on the server
 * Triggered by local edits, server hints (SSE), reconnecting, returning to the
 * app, and a slow periodic timer.
 */
class SyncEngine {
  private status: SyncStatus = { phase: 'idle', lastSyncedAt: null, message: null };
  private listeners = new Set<Listener>();
  private options: SyncStartOptions | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private rerun = false;
  private events: EventSource | null = null;
  private eventsRetry: ReturnType<typeof setTimeout> | null = null;
  private eventsBackoffMs = 2_000;
  /** Retry delay while the server is unreachable; grows to a minute, resets on success. */
  private offlineBackoffMs = 3_000;
  private cleanup: (() => void)[] = [];
  readonly rejections = new Set<(messages: string[]) => void>();
  readonly notificationListeners = new Set<(n: AppNotification) => void>();

  getStatus = () => this.status;
  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private setStatus(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    this.listeners.forEach((l) => l());
  }

  start(options: SyncStartOptions) {
    this.stop();
    this.options = options;
    const onOnline = () => this.schedule(0);
    const onVisible = () => document.visibilityState === 'visible' && this.schedule(0);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    this.cleanup.push(
      () => window.removeEventListener('online', onOnline),
      () => document.removeEventListener('visibilitychange', onVisible),
    );
    this.interval = setInterval(() => document.visibilityState === 'visible' && this.schedule(0), 60_000);
    this.connectEvents();
    // Mutations left "sending" by a closed tab are safe to resend: the server dedupes by mutation id.
    void db.outbox
      .filter((e) => e.state === 'sending')
      .modify({ state: 'queued' })
      .finally(() => this.schedule(0));
  }

  stop() {
    this.cleanup.forEach((fn) => fn());
    this.cleanup = [];
    if (this.timer) clearTimeout(this.timer);
    if (this.interval) clearInterval(this.interval);
    if (this.eventsRetry) clearTimeout(this.eventsRetry);
    this.eventsRetry = null;
    this.events?.close();
    this.events = null;
    this.timer = null;
    this.interval = null;
    this.options = null;
  }

  /** Debounced sync request. */
  schedule(delayMs = 300) {
    if (!this.options) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.syncNow(), delayMs);
  }

  private currentRun: Promise<void> | null = null;

  /** Sync now; if a sync is already running, wait for it (and run once more afterwards). */
  syncNow(): Promise<void> {
    if (!this.options) return Promise.resolve();
    if (this.currentRun) {
      this.rerun = true;
      return this.currentRun;
    }
    this.currentRun = this.runSync().finally(() => {
      this.currentRun = null;
    });
    return this.currentRun;
  }

  private async runSync(): Promise<void> {
    this.setStatus({ phase: 'syncing' });
    const run = async () => {
      try {
        for (const family of this.options?.families ?? []) {
          await this.pushFamily(family.id);
          await this.pullFamily(family.id);
        }
        await processUploads();
        this.offlineBackoffMs = 3_000;
        this.setStatus({ phase: 'idle', lastSyncedAt: Date.now(), message: null });
        if (!this.events) this.connectEvents();
      } catch (err) {
        if (err instanceof OfflineError) {
          this.setStatus({ phase: 'offline', message: null });
          // Keep trying while unreachable, backing off so we don't hammer a recovering server.
          this.schedule(this.offlineBackoffMs);
          this.offlineBackoffMs = Math.min(this.offlineBackoffMs * 2, 60_000);
        } else {
          console.warn('sync failed', err);
          this.setStatus({ phase: 'error', message: err instanceof Error ? err.message : 'Sync failed' });
        }
      }
    };
    try {
      // Only one tab syncs at a time; others rely on it (they share the database).
      if (typeof navigator !== 'undefined' && navigator.locks) await navigator.locks.request('app-sync', run);
      else await run();
    } finally {
      if (this.rerun) {
        this.rerun = false;
        this.schedule(50);
      }
    }
  }

  private connectEvents() {
    if (typeof EventSource === 'undefined') return;
    const es = new EventSource('/api/sync/stream');
    es.addEventListener('change', (e) => {
      const { familyId } = JSON.parse((e as MessageEvent).data) as { familyId: string };
      if (this.options?.families.some((f) => f.id === familyId)) this.schedule(150);
    });
    es.addEventListener('notification', (e) => {
      const n = JSON.parse((e as MessageEvent).data) as AppNotification;
      void db.notifications.put(n);
      this.notificationListeners.forEach((l) => l(n));
    });
    es.addEventListener('membership', () => this.options?.onMembershipChange());
    es.addEventListener('ready', () => {
      this.eventsBackoffMs = 2_000;
      this.schedule(0);
    });
    // Browsers retry network drops themselves, but give up for good after an
    // HTTP error (e.g. a proxy's 502 while the server restarts). Reconnect then.
    es.onerror = () => {
      if (es.readyState !== EventSource.CLOSED || !this.options) return;
      this.events = null;
      if (this.eventsRetry) clearTimeout(this.eventsRetry);
      this.eventsRetry = setTimeout(() => {
        this.eventsRetry = null;
        if (this.options && !this.events) this.connectEvents();
      }, this.eventsBackoffMs);
      this.eventsBackoffMs = Math.min(this.eventsBackoffMs * 2, 60_000);
    };
    this.events = es;
  }

  private async pushFamily(familyId: string) {
    for (;;) {
      const queued = (await db.outbox.where('familyId').equals(familyId).toArray())
        .filter((e) => e.state === 'queued')
        .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
        .slice(0, 100);
      if (queued.length === 0) return;
      await db.outbox.bulkUpdate(queued.map((e) => ({ key: e.seq!, changes: { state: 'sending' as const, attempts: e.attempts + 1 } })));
      let res: PushResponse;
      try {
        res = await api<PushResponse>('/api/sync/push', {
          method: 'POST',
          body: {
            familyId,
            mutations: queued.map((e) => ({
              mid: e.mid,
              collection: e.collection,
              id: e.recordId,
              op: e.op,
              patch: e.patch,
              baseRev: e.baseRev,
              clientTime: e.clientTime,
            })),
          },
        });
      } catch (err) {
        await db.outbox.bulkUpdate(queued.map((e) => ({ key: e.seq!, changes: { state: 'queued' as const } })));
        if (err instanceof ApiError && err.status === 404) return this.options?.onFamilyGone(familyId);
        if (err instanceof ApiError && err.status === 400) {
          // A malformed batch can't succeed by retrying; drop entries that keep failing.
          const stuck = queued.filter((e) => e.attempts >= 4).map((e) => e.seq!);
          if (stuck.length) await db.outbox.bulkDelete(stuck);
        }
        throw err;
      }
      learnServerTime(res.serverTime);
      await this.applyPushResults(familyId, queued, res.results);
    }
  }

  private async applyPushResults(familyId: string, batch: OutboxEntry[], results: MutationResult[]) {
    const byMid = new Map(batch.map((e) => [e.mid, e]));
    const messages: string[] = [];
    await db.transaction('rw', db.tables, async () => {
      for (const r of results) {
        const entry = byMid.get(r.mid);
        if (!entry) continue;
        await db.outbox.delete(entry.seq!);
        const table = db.records(entry.collection);
        if (r.status === 'applied') {
          const remaining = await db.outbox.where('[collection+recordId]').equals([entry.collection, entry.recordId]).toArray();
          // Later local edits were made on top of this one: rebase them onto the new revision.
          for (const e of remaining) if (e.state === 'queued') await db.outbox.update(e.seq!, { baseRev: r.record.rev });
          const pending = await pendingPatch(entry.collection, entry.recordId);
          if (!pending?.deleting) await table.put({ ...r.record, ...(pending?.patch ?? {}) });
          for (const c of r.conflicts ?? []) {
            await db.conflicts.put({
              id: `${entry.collection}:${entry.recordId}:${c.field}`,
              familyId,
              collection: entry.collection,
              recordId: entry.recordId,
              kind: 'field',
              field: c.field,
              theirs: c.theirs,
              yours: c.yours,
              createdAt: new Date().toISOString(),
            });
          }
        } else if (r.status === 'deleted') {
          const pending = await pendingPatch(entry.collection, entry.recordId);
          if (!pending || pending.deleting) await table.delete(entry.recordId);
        } else {
          if (r.code === 'deleted') {
            const snapshot = await table.get(entry.recordId);
            await table.delete(entry.recordId);
            await db.conflicts.put({
              id: `${entry.collection}:${entry.recordId}:deleted`,
              familyId,
              collection: entry.collection,
              recordId: entry.recordId,
              kind: 'deleted',
              snapshot,
              yours: entry.patch,
              createdAt: new Date().toISOString(),
            });
          } else if (r.record) {
            await table.put(r.record);
            messages.push(r.message);
          } else {
            if (entry.op !== 'delete') await table.delete(entry.recordId);
            messages.push(r.message);
          }
        }
      }
    });
    if (messages.length) this.rejections.forEach((l) => l(messages));
  }

  private async pullFamily(familyId: string) {
    let cursor = (await getMeta<number>(META.cursor(familyId))) ?? 0;
    const storedEpoch = await getMeta<number>(META.epoch(familyId));
    let full = false;
    const seen = new Set<string>();
    let last: PullResponse | undefined;
    for (;;) {
      let res: PullResponse;
      try {
        res = await api<PullResponse>(`/api/sync/pull?familyId=${familyId}&since=${cursor}&limit=500`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return this.options?.onFamilyGone(familyId);
        throw err;
      }
      learnServerTime(res.serverTime);
      if (!full && (res.reset || (storedEpoch !== undefined && res.accessEpoch !== storedEpoch))) {
        // What this person may see changed (role change, restored server): rebuild from scratch.
        full = true;
        cursor = 0;
        continue;
      }
      await db.transaction('rw', db.tables, async () => {
        for (const change of res.changes) {
          const table = db.records(change.collection);
          if (change.record) {
            seen.add(`${change.collection}:${change.record.id}`);
            const pending = await pendingPatch(change.collection, change.record.id);
            if (pending?.deleting) continue;
            // Don't resurrect items inside a container the user just deleted here.
            const def = getCollection(change.collection);
            const parent = def ? parentPointer(def, change.record) : null;
            if (parent && (await pendingPatch(parent.collection, parent.id))?.deleting) continue;
            await table.put({ ...change.record, ...(pending?.patch ?? {}) });
          } else if (change.deleted) {
            await table.delete(change.deleted.id);
          }
        }
        for (const removal of res.removals) {
          await db.records(removal.collection).delete(removal.id);
          const stale = await db.outbox.where('[collection+recordId]').equals([removal.collection, removal.id]).primaryKeys();
          await db.outbox.bulkDelete(stale);
        }
      });
      cursor = res.cursor;
      last = res;
      if (!res.hasMore) break;
    }
    if (full) {
      await db.transaction('rw', db.tables, async () => {
        for (const c of ALL_COLLECTIONS) {
          const ids = await db.records(c.name).where('familyId').equals(familyId).primaryKeys();
          for (const id of ids) {
            if (seen.has(`${c.name}:${id}`)) continue;
            if (await pendingPatch(c.name, id)) continue;
            await db.records(c.name).delete(id);
          }
        }
      });
    }
    await setMeta(META.cursor(familyId), cursor);
    if (last) await setMeta(META.epoch(familyId), last.accessEpoch);
    await setMeta(META.lastSync(familyId), Date.now());
  }
}

export const syncEngine = new SyncEngine();
